// ============================================================
// SecaoPlanilhaTreino.js — a planilha de treino da preparação física
//
// O plano trimestral combina o mês e o microciclo. Aqui vem o degrau
// seguinte: o treino escrito exercício por exercício, uma aba por dia, para
// o preparador levar impresso e conferir na sala.
//
// A tela é uma grade de propósito. Formulário com um cartão por exercício
// foi o primeiro desenho e não serve: o preparador compara linhas entre si
// o tempo todo ("a carga de terça está maior que a de segunda?"), e
// comparação pede coluna alinhada, não cartão empilhado.
//
// A carga de cada semana é CONTA, não campo. Sai da carga base e da
// progressão e muda sozinha quando a base é corrigida. Guardada pronta,
// continuaria dizendo o valor antigo, e ninguém confere quatro números
// depois de mexer num quinto.
// ============================================================

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { listarPlanilhasTreino, salvarPlanilhaTreino, carregarPresencaFisicaDoMes } from "../firebase";
import { gerarPDFPlanilhaTreino } from "../pdfRelatorio";
import { semanasDaLinha, semanasDaCarga, lerCarga, SEMANAS } from "../cargaDeTreino";
import { useNomeNucleo } from "../NucleosContexto";
import {
  MESES, AZUL, AZUL_ESCURO, AZUL_CLARO, TEXTO, CINZA_ESC, BRANCO, PERIGO,
  OK, OK_CL, BORDA, FUNDO_SUAVE, ATENCAO } from "../tema";
import { Card, StatusBox, Btn } from "../componentes";

const DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);
const ESPERA_PARA_SALVAR = 1200;

// Duas quebras de linha, para separar parágrafos numa caixa de aviso.
// Montada por código: barra invertida solta dentro de texto some em
// algumas ferramentas de edição e parte a linha no meio.
const QUEBRA_DUPLA = String.fromCharCode(10) + String.fromCharCode(10);

const DIAS = [
  { chave: "seg", curto: "Segunda", longo: "Segunda-feira" },
  { chave: "ter", curto: "Terça", longo: "Terça-feira" },
  { chave: "qua", curto: "Quarta", longo: "Quarta-feira" },
  { chave: "qui", curto: "Quinta", longo: "Quinta-feira" },
  { chave: "sex", curto: "Sexta", longo: "Sexta-feira" },
];

// A sessão na ordem em que acontece. A ordem aqui é a ordem no papel.
const BLOCOS = [
  { chave: "aquecimento", rotulo: "Aquecimento", ondeEntra: "no aquecimento" },
  { chave: "principal", rotulo: "Parte principal", ondeEntra: "na parte principal" },
  { chave: "final", rotulo: "Volta à calma", ondeEntra: "na volta à calma" },
];

// As colunas, com o vocabulário da preparação física. Volume virou duas
// colunas, séries e repetições, porque série soma e vira o total do dia;
// num campo de texto só, o total se perde.
const COLUNAS = [
  { chave: "objetivo", rotulo: "Objetivo", dica: "Capacidade a desenvolver", larg: "1.3fr" },
  { chave: "exercicio", rotulo: "Exercício / método", dica: "O que será feito", larg: "1.8fr" },
  { chave: "series", rotulo: "Séries", larg: "60px", centro: true },
  { chave: "repeticoes", rotulo: "Repetições", larg: "84px", centro: true },
  { chave: "intervalo", rotulo: "Intervalo", larg: "82px", centro: true },
  { chave: "carga", rotulo: "Carga base", larg: "92px", centro: true },
  { chave: "progressao", rotulo: "+ % / sem", larg: "72px", centro: true },
];
const LARGURA_DA_SEMANA = "62px";

// O id que o servidor dá ao documento da planilha. Reproduzido aqui só
// para o caso de a resposta vir sem ele: o que importa é ser estável para o
// mesmo trio modalidade/mês/alvo, porque é ele que diz "esta linha da lista
// é a mesma de antes" na hora de substituir.
function idLocalDaPlanilha(pedido) {
  return ["local", chave(pedido.modalidade), String(pedido.anoMes || ""), String(pedido.alvo || "turma")].join("__");
}

// A planilha recém-gravada entrando na lista que a tela já tem em mão.
//
// Sem isto, salvar não deixava marca nenhuma: `dados` continuava sendo a
// resposta do servidor de ANTES do salvamento, então o atleta seguia
// "herdando o treino da turma", não aparecia marcado no seletor e não
// entrava na lista do mês. E havia prejuízo, não só falta de aviso: sair
// para a turma e voltar ao atleta trazia o treino DA TURMA de novo, porque
// era nele que a tela continuava baseada, e salvar em cima apagava o
// individual que tinha acabado de ser escrito.
//
// Registrar aqui, em vez de reler tudo do servidor, porque a releitura
// chega no meio da digitação seguinte e sobrescreve o que está sendo
// escrito. O que foi gravado é exatamente o que acabamos de enviar, então
// não há nada a perguntar.
function comPlanilhaGravada(dados, pedido, id, quemSalvou) {
  if (!dados) return dados;
  const alvoDoPedido = pedido.alvo || "turma";
  const sobreviventes = (dados.planilhas || []).filter(
    (x) => x.id !== id
      && !(chave(x.modalidade) === chave(pedido.modalidade) && (x.alvo || "turma") === alvoDoPedido)
  );
  return {
    ...dados,
    planilhas: [...sobreviventes, {
      id,
      modalidade: pedido.modalidade,
      alvo: alvoDoPedido,
      alvoNome: pedido.alvoNome || "",
      dias: (pedido.conteudo && pedido.conteudo.dias) || {},
      atualizadoPorNome: quemSalvou || "",
      atualizadoEm: new Date().toISOString(),
    }],
  };
}

const LINHA_NOVA = {
  bloco: "principal", objetivo: "", exercicio: "", series: "",
  repeticoes: "", intervalo: "", carga: "", progressao: "",
  semanas: ["", "", "", ""],
};

function chave(m) {
  return String(m || "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function nomeDoMes(anoMes) {
  const p = String(anoMes || "").split("-");
  if (p.length !== 2) return String(anoMes || "");
  return `${MESES[Number(p[1]) - 1] || p[1]} de ${p[0]}`;
}

// Os meses que a tela oferece: dois atrás, o atual e três à frente.
//
// Atrás porque o preparador consulta o que passou; à frente porque ele
// escreve o mês seguinte antes de ele começar, que é o normal.
function mesesDisponiveis(hojeISO) {
  const base = /^\d{4}-\d{2}/.test(String(hojeISO || "")) ? hojeISO : new Date().toISOString();
  const ano = Number(String(base).slice(0, 4));
  const mes = Number(String(base).slice(5, 7));
  return [-2, -1, 0, 1, 2, 3].map((d) => {
    const total = (ano * 12) + (mes - 1) + d;
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
  });
}

// ── A execução do treino, por atleta ──────────────────────────
//
// Não existe marcação nova para isto, e não devia existir: a execução já é
// registrada todo dia na presença do treino físico, que a preparação
// física marca atleta por atleta. Criar um segundo lugar para dizer a
// mesma coisa dobraria o trabalho da sala e deixaria os dois discordando
// na primeira semana corrida.
//
// O que faltava era trazer esse registro para o lado do treino escrito: a
// planilha diz o que era para ser feito na segunda, e a presença diz em
// quantas segundas foi feito.

// A chave do nome, igual à que a presença física grava. Acento não é
// removido aqui de propósito: o documento gravado usa exatamente esta
// conta, e "José" vira "jos_" nos dois lados.
export function chaveDeNome(nome) {
  return String(nome || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
}

const DIA_DA_SEMANA = { seg: 1, ter: 2, qua: 3, qui: 4, sex: 5 };

// As datas de um mês que caem no dia da semana da aba aberta.
export function datasDoDiaNoMes(anoMes, chaveDoDia) {
  const alvo = DIA_DA_SEMANA[chaveDoDia];
  if (!alvo || !/^\d{4}-\d{2}$/.test(String(anoMes || ""))) return [];
  const ano = Number(String(anoMes).slice(0, 4));
  const mes = Number(String(anoMes).slice(5, 7));
  // "2026-13" passa no formato e não existe. Sem esta linha o Date rola
  // para janeiro do ano seguinte e a tela mostraria datas do mês errado
  // como se fossem deste.
  if (!(mes >= 1 && mes <= 12)) return [];
  const datas = [];
  // Dia 0 do mês seguinte é o último deste. Evita a tabela de 28/30/31 e
  // acerta fevereiro bissexto sem caso especial.
  const ultimo = new Date(ano, mes, 0).getDate();
  for (let d = 1; d <= ultimo; d++) {
    if (new Date(ano, mes - 1, d).getDay() === alvo) {
      datas.push(`${anoMes}-${String(d).padStart(2, "0")}`);
    }
  }
  return datas;
}

// Quantas vezes o atleta executou o treino daquele dia no mês.
//
// `marcadas` é o denominador honesto: conta só as datas em que alguém
// marcou presença ou falta. A data sem marcação nenhuma aparece na lista
// como tal, e não como falta: ninguém disse que ele não foi, apenas não
// houve registro, e tratar uma coisa pela outra inventa falta.
export function execucoesDoAtleta({ execucoes, nome, anoMes, chaveDoDia }) {
  const mapa = execucoes || {};
  const nomeEmChave = chaveDeNome(nome);
  const datas = datasDoDiaNoMes(anoMes, chaveDoDia).map((data) => ({
    data,
    status: mapa[`${nomeEmChave}__${data}`] || "",
  }));
  return {
    datas,
    presentes: datas.filter((d) => d.status === "presente").length,
    faltas: datas.filter((d) => d.status === "falta").length,
    marcadas: datas.filter((d) => d.status).length,
  };
}

function resumoDoDia(dia) {
  const linhas = (dia && Array.isArray(dia.linhas)) ? dia.linhas : [];
  const series = linhas.reduce((soma, l) => {
    const n = Number(String(l.series || "").replace(",", "."));
    return soma + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  return { exercicios: linhas.length, series: Math.round(series * 10) / 10 };
}

function planilhaVazia() {
  const dias = {};
  DIAS.forEach((d) => { dias[d.chave] = { observacao: "", linhas: [] }; });
  return { dias, semanasExecutadas: [false, false, false, false] };
}

// A linha do mês anterior que corresponde a esta.
//
// Casada pelo NOME do exercício, e não pela posição: acrescentar um
// exercício no meio do mês novo deslocaria todas as posições e cada linha
// passaria a mostrar a carga de outra. Sem correspondência, as quatro
// células do mês anterior ficam vazias, que é o que acontece com um
// exercício que entrou agora.
function linhaDoMesAnterior(anterior, chaveDoDia, exercicio) {
  const nome = chave(exercicio);
  if (!nome || !anterior || !anterior.dias) return null;
  const doDia = (anterior.dias[chaveDoDia] || {}).linhas;
  if (!Array.isArray(doDia)) return null;
  return doDia.find((l) => chave(l.exercicio) === nome) || null;
}

// Quatro colunas de semana, ou oito quando o mês anterior está ao lado.
function gradeDaLinha(comAnterior) {
  return [
    ...COLUNAS.map((c) => c.larg),
    ...Array.from({ length: comAnterior ? SEMANAS * 2 : SEMANAS }, () => LARGURA_DA_SEMANA),
    "58px",
  ].join(" ");
}

const estiloCelula = {
  width: "100%", border: "none", borderRight: `1px solid ${BORDA}`,
  padding: "9px 8px", fontSize: 12.5, fontFamily: "inherit",
  color: TEXTO, background: "transparent", outline: "none", boxSizing: "border-box",
};

// O cabeçalho da grade.
//
// Quando há mês anterior, as quatro semanas dele vêm antes das quatro
// deste mês, numeradas de 1 a 8: a periodização não recomeça do zero a
// cada mês, e a carga da semana 5 sai da 4.
//
// Cada semana DESTE mês tem o botão de executada. Marcada, a coluna trava:
// treino que já aconteceu é registro, e corrigir para trás reescreve o que
// o atleta fez. Desmarcar reabre, porque erro de digitação existe.
function Cabecalho({ comAnterior, mesAnterior, executadas, onExecutada, somenteLeitura }) {
  const rotuloCurto = (anoMes) => {
    const p = String(anoMes || "").split("-");
    return p.length === 2 ? `${MESES[Number(p[1]) - 1] || p[1]}`.slice(0, 3) : "anterior";
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: gradeDaLinha(comAnterior), background: FUNDO_SUAVE, borderBottom: `1px solid ${BORDA}` }}>
      {COLUNAS.map((c) => (
        <div key={c.chave} style={{
          fontSize: 10, fontWeight: 800, color: CINZA_ESC, textTransform: "uppercase",
          letterSpacing: ".04em", padding: "9px 8px", textAlign: c.centro ? "center" : "left",
        }}>{c.rotulo}</div>
      ))}

      {comAnterior && Array.from({ length: SEMANAS }, (_, i) => (
        <div key={`ant${i}`} title={`Semana ${i + 1}, de ${mesAnterior}. Mês fechado, só leitura.`}
          style={{
            fontSize: 10, fontWeight: 800, color: CINZA_ESC, textTransform: "uppercase",
            letterSpacing: ".04em", padding: "9px 4px", textAlign: "center", background: "#EDEFF3",
          }}>
          Sem {i + 1}
          <div style={{ fontSize: 8.5, fontWeight: 700, opacity: 0.8 }}>{rotuloCurto(mesAnterior)}</div>
        </div>
      ))}

      {Array.from({ length: SEMANAS }, (_, i) => (
        <div key={i} style={{
          fontSize: 10, fontWeight: 800, color: AZUL_ESCURO, textTransform: "uppercase",
          letterSpacing: ".04em", padding: "7px 4px", textAlign: "center",
          background: executadas[i] ? "#DCE8DC" : AZUL_CLARO,
        }}>
          Sem {comAnterior ? i + 1 + SEMANAS : i + 1}
          {!somenteLeitura && (
            <button type="button" onClick={() => onExecutada(i, !executadas[i])}
              title={executadas[i]
                ? "Semana executada. Clique para reabrir e poder corrigir."
                : "Marcar como executada. A coluna trava para edição."}
              style={{
                display: "block", margin: "3px auto 0", border: "none", borderRadius: 6,
                padding: "2px 6px", fontSize: 8.5, fontWeight: 800, cursor: "pointer",
                fontFamily: "inherit", letterSpacing: ".02em",
                background: executadas[i] ? OK : BRANCO,
                color: executadas[i] ? BRANCO : CINZA_ESC,
              }}>
              {executadas[i] ? "✓ feita" : "marcar"}
            </button>
          )}
        </div>
      ))}
      <div />
    </div>
  );
}

// "2026-09-28T19:43:02.000Z" vira "28/09 às 16:43". Data E hora porque o
// treino é revisto no mesmo dia: só a data não distingue a versão da manhã
// da versão de depois da conversa com o técnico.
function dataHoraCurta(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || "");
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  return `${dia}/${mes} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function SecaoPlanilhaTreino({ userNucleo, podeEditar = true }) {
  const nomeNucleo = useNomeNucleo();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [status, setStatus] = useState(null);
  const [anoMes, setAnoMes] = useState("");
  const [modalidadeAtiva, setModalidadeAtiva] = useState("");
  const [alvo, setAlvo] = useState("turma");
  const [diaAtivo, setDiaAtivo] = useState("seg");
  const [conteudo, setConteudo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState(null);
  const [baixando, setBaixando] = useState(false);
  const [copiarDe, setCopiarDe] = useState("");
  // A presença do treino físico do mês aberto, para contar quantas vezes o
  // atleta executou o treino do dia. Carregada só quando alguém abre um
  // atleta: na turma toda ela não aparece, e leitura que ninguém vê é
  // leitura cobrada à toa.
  const [execucoes, setExecucoes] = useState(null);
  const [mesDasExecucoes, setMesDasExecucoes] = useState("");
  const relogio = useRef(null);

  // O que está esperando para ser gravado, com o contexto em que foi
  // digitado.
  //
  // Antes era só um setTimeout: sair da tela, trocar de mês, de modalidade
  // ou de atleta cancelava a gravação pendente e o que tinha acabado de ser
  // digitado ia embora sem aviso. Guardar o pedido inteiro — e não só o
  // conteúdo — é o que permite gravá-lo DEPOIS de a tela já ter mudado de
  // assunto, no contexto certo.
  const pendente = useRef(null);
  const aplicado = useRef("");
  // Em ref, e não em dependência: o nome de quem salva não muda durante a
  // sessão, e amarrá-lo à gravação faria a função de gravar nascer de novo
  // a cada releitura, junto com o efeito que grava ao trocar de atleta.
  const meuNome = useRef("");

  const recarregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await listarPlanilhasTreino({ nucleo: userNucleo, anoMes: anoMes || undefined });
      setDados(r);
      if (!anoMes && r.anoMes) setAnoMes(r.anoMes);
    } catch (e) {
      setErro("Não foi possível carregar: " + (e.message || ""));
      setDados({ modalidades: [], planilhas: [], contexto: {} });
    }
  }, [userNucleo, anoMes]);

  useEffect(() => { recarregar(); }, [recarregar]);

  useEffect(() => {
    if (dados && dados.meuNome) meuNome.current = dados.meuNome;
  }, [dados]);

  useEffect(() => {
    if (!dados || modalidadeAtiva) return;
    setModalidadeAtiva((dados.modalidades || [])[0] || "");
  }, [dados, modalidadeAtiva]);

  // O alvo pertence à modalidade aberta, sempre.
  //
  // Sem isto, abrir um atleta do BMX e depois clicar em Skate deixava o
  // nome dele preso no alto da tela e no seletor "Vale para", em cima de
  // uma modalidade que não é a dele. A tela ficava dizendo duas coisas
  // diferentes ao mesmo tempo, e o treino mostrado era o da turma.
  //
  // A planilha gravada também vale como pertencimento: atleta que mudou de
  // modalidade ou saiu do cadastro continua tendo o treino dele aberto por
  // quem clica na lista do mês, em vez de o clique voltar para a turma.
  useEffect(() => {
    if (!dados || alvo === "turma" || !modalidadeAtiva) return;
    const noCadastro = (dados.atletas || []).some(
      (a) => a.uid === alvo && chave(a.modalidade) === chave(modalidadeAtiva)
    );
    const temPlanilha = (dados.planilhas || []).some(
      (p) => p.alvo === alvo && chave(p.modalidade) === chave(modalidadeAtiva)
    );
    if (!noCadastro && !temPlanilha) setAlvo("turma");
  }, [dados, modalidadeAtiva, alvo]);

  // A presença do treino físico do mês, só quando há um atleta aberto.
  useEffect(() => {
    if (alvo === "turma" || !anoMes || !userNucleo) return;
    if (mesDasExecucoes === anoMes) return;
    let ativo = true;
    (async () => {
      try {
        const mapa = await carregarPresencaFisicaDoMes(userNucleo, anoMes);
        if (!ativo) return;
        setExecucoes(mapa);
        setMesDasExecucoes(anoMes);
      } catch (e) {
        // A planilha é o assunto da tela; a contagem é apoio. Falhar aqui
        // não pode esconder o treino, então a tela segue sem o número.
        if (!ativo) return;
        setExecucoes({});
        setMesDasExecucoes(anoMes);
      }
    })();
    return () => { ativo = false; };
  }, [alvo, anoMes, userNucleo, mesDasExecucoes]);

  const planilha = useMemo(() => {
    if (!dados || !modalidadeAtiva) return null;
    return (dados.planilhas || []).find(
      (p) => chave(p.modalidade) === chave(modalidadeAtiva) && (p.alvo || "turma") === alvo
    ) || null;
  }, [dados, modalidadeAtiva, alvo]);

  // A planilha da turma da mesma modalidade e mês. É a base do treino
  // individual: quando o preparador abre um atleta que ainda não tem
  // planilha própria, aparece o treino da turma para ele ajustar, em vez de
  // uma folha em branco que obriga a redigitar tudo.
  const daTurma = useMemo(() => {
    if (!dados || !modalidadeAtiva) return null;
    return (dados.planilhas || []).find(
      (x) => chave(x.modalidade) === chave(modalidadeAtiva) && (x.alvo || "turma") === "turma"
    ) || null;
  }, [dados, modalidadeAtiva]);

  // A planilha do MÊS ANTERIOR, do mesmo alvo e da mesma modalidade.
  //
  // Serve de leitura, ao lado das semanas deste mês: a carga da semana 5
  // sai da 4, e ler isso obrigava a trocar de mês, anotar e voltar.
  //
  // Atleta sem planilha própria no mês passado cai na da turma, pelo mesmo
  // motivo que o treino dele herda a da turma aqui.
  const doMesAnterior = useMemo(() => {
    const lista = (dados && dados.anteriores) || [];
    if (!modalidadeAtiva || !lista.length) return null;
    const daModalidade = lista.filter((x) => chave(x.modalidade) === chave(modalidadeAtiva));
    return daModalidade.find((x) => (x.alvo || "turma") === alvo)
      || (alvo !== "turma" ? daModalidade.find((x) => (x.alvo || "turma") === "turma") : null)
      || null;
  }, [dados, modalidadeAtiva, alvo]);

  // O que a tela mostra: a planilha do alvo, ou — para um atleta sem
  // planilha própria — a da turma, ainda não gravada em nome dele.
  const base = planilha || (alvo !== "turma" ? daTurma : null);
  const herdadoDaTurma = !planilha && Boolean(base);

  function diasDe(origem) {
    const dias = { ...planilhaVazia().dias };
    if (origem && origem.dias) {
      DIAS.forEach((d) => {
        const bruto = origem.dias[d.chave] || {};
        dias[d.chave] = {
          observacao: bruto.observacao || "",
          linhas: Array.isArray(bruto.linhas) ? bruto.linhas.map((l) => ({ ...LINHA_NOVA, ...l })) : [],
        };
      });
    }
    return dias;
  }

  // O conteúdo em edição é trocado quando a tela muda de assunto: outra
  // modalidade, outro mês, outro atleta, ou quando a planilha daquele
  // assunto chega do servidor.
  //
  // A marca existe para NÃO sobrescrever o que está sendo digitado. Antes o
  // efeito dependia do objeto da planilha, que nasce de novo a cada
  // releitura: bastava o servidor responder no meio da digitação para o
  // campo voltar ao que estava gravado.
  useEffect(() => {
    const marca = [chave(modalidadeAtiva), alvo, anoMes, (base && base.id) || "vazia"].join("|");
    if (aplicado.current === marca) return;
    aplicado.current = marca;
    setConteudo({
      dias: diasDe(base),
      semanasExecutadas: (base && base.semanasExecutadas) || [false, false, false, false],
    });
    setSalvoEm(null);
    setCopiarDe("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, modalidadeAtiva, anoMes, alvo]);

  const somenteLeitura = !podeEditar || (dados ? dados.podeEditar === false : false);

  const executadas = (conteudo && conteudo.semanasExecutadas) || [false, false, false, false];

  // Tem exercício escrito em algum dia? Decide se o botão de repetir
  // avisa que vai substituir, e se ele aparece em destaque.
  const temConteudo = DIAS.some(
    (d) => (((conteudo && conteudo.dias) || {})[d.chave] || {}).linhas?.length > 0
  );

  // Marcar ou desmarcar uma semana como executada.
  //
  // Trava a coluna para edição, e é só isso: não apaga nem congela nada no
  // servidor. O treino que já aconteceu é registro, e corrigir para trás
  // reescreve o que o atleta fez; quem precisa corrigir desmarca primeiro.
  function marcarExecutada(indice, valor) {
    setConteudo((atual) => {
      const lista = [...((atual && atual.semanasExecutadas) || [false, false, false, false])];
      lista[indice] = Boolean(valor);
      const novo = { ...atual, semanasExecutadas: lista };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // `aindaNaTela` é falso quando a gravação é a de um assunto que a tela
  // acabou de deixar — trocou de atleta, de mês ou de modalidade. A
  // diferença importa: só o que está em edição pode ter a marca adiantada,
  // e adiantar a do assunto que saiu reescreveria o campo do assunto novo.
  const gravar = useCallback(async (pedido, aindaNaTela = true) => {
    if (!pedido) return;
    setSalvando(true);
    try {
      const r = await salvarPlanilhaTreino(pedido);
      if (pendente.current === pedido) pendente.current = null;
      const id = (r && r.planilhaId) || idLocalDaPlanilha(pedido);

      // A marca passa a ser a da planilha nova ANTES de o estado mudar.
      // Sem isso, o efeito que troca o conteúdo da tela veria um assunto
      // "novo" — o mesmo atleta, agora com planilha própria — e reescreveria
      // os campos, engolindo o que foi digitado depois do salvamento.
      if (aindaNaTela) {
        aplicado.current = [chave(pedido.modalidade), pedido.alvo, pedido.anoMes, id].join("|");
      }
      setDados((atual) => comPlanilhaGravada(atual, pedido, id, meuNome.current));
      setSalvoEm(new Date());
      setStatus(null);
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Não foi possível salvar: " + (e.message || "") });
    } finally {
      setSalvando(false);
    }
    // Sem dependência nenhuma, de propósito: tudo o que esta função precisa
    // saber vem no pedido. Estável, ela não faz o efeito que grava ao sair
    // da tela nascer de novo a cada releitura.
  }, []);

  const agendarSalvamento = useCallback((novo) => {
    if (!modalidadeAtiva || !anoMes || somenteLeitura) return;
    pendente.current = {
      modalidade: modalidadeAtiva, anoMes, alvo,
      alvoNome: alvo === "turma" ? "" : nomeDoAlvo(dados, alvo),
      conteudo: novo,
    };
    if (relogio.current) clearTimeout(relogio.current);
    relogio.current = setTimeout(() => gravar(pendente.current), ESPERA_PARA_SALVAR);
  }, [modalidadeAtiva, anoMes, alvo, dados, somenteLeitura, gravar]);

  // Salvar agora, sem esperar a pausa de digitação. É o botão, e é também
  // o que roda antes de a tela trocar de assunto.
  const salvarAgora = useCallback(async () => {
    if (relogio.current) { clearTimeout(relogio.current); relogio.current = null; }

    // Sem alteração pendente o botão grava do mesmo jeito. Quem abre um
    // atleta que herda o treino da turma, não muda nada e clica em Salvar
    // está pedindo exatamente isso: que aquele treino passe a ser dele.
    // Antes o clique não fazia nada e a tela não dizia por quê.
    const pedido = pendente.current || (
      conteudo && !somenteLeitura && modalidadeAtiva && anoMes
        ? {
          modalidade: modalidadeAtiva, anoMes, alvo,
          alvoNome: alvo === "turma" ? "" : nomeDoAlvo(dados, alvo),
          conteudo,
        }
        : null
    );
    if (!pedido) return;
    pendente.current = pedido;
    await gravar(pedido);
  }, [gravar, conteudo, somenteLeitura, modalidadeAtiva, anoMes, alvo, dados]);

  // Trocar de modalidade, de mês ou de atleta grava o que estava pendente
  // ANTES de a tela trocar de conteúdo. A limpeza do efeito roda antes do
  // próximo, então o pedido guardado ainda é o do contexto que está saindo.
  useEffect(() => () => {
    if (relogio.current) { clearTimeout(relogio.current); relogio.current = null; }
    if (pendente.current) {
      const pedido = pendente.current;
      pendente.current = null;
      // Por `gravar`, e não pela chamada crua: assim o treino que estava
      // sendo escrito ao sair entra na lista da tela também, e voltar para
      // o atleta encontra o que ele tem, não o da turma.
      gravar(pedido, false).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalidadeAtiva, anoMes, alvo, gravar]);

  // Copiar o treino de outro atleta (ou o da turma) para o que está aberto.
  //
  // Sem isto, montar o treino de dez atletas a partir de um só significa
  // redigitar dez vezes a mesma coisa para mudar duas cargas.
  // Repetir o treino do mês anterior, com as cargas.
  //
  // O mês seguinte raramente começa do zero: o treino é o mesmo e o que
  // muda é a carga, que sobe. Redigitar quarenta linhas para mexer em três
  // números é o trabalho que este botão tira.
  //
  // Vem com as CARGAS, e é isso que o preparador pediu: a carga base de
  // cada exercício passa a ser a que a última semana do mês anterior
  // atingiu, que é onde o atleta efetivamente está. Começar o mês novo na
  // carga base do mês velho seria mandar o atleta repetir quatro semanas.
  //
  // As correções semana a semana NÃO vêm junto: elas são o registro do que
  // aconteceu naquele mês, e repeti-las no mês novo seria afirmar um
  // treino que ainda não houve. As semanas executadas também nascem
  // limpas, pelo mesmo motivo.
  function repetirMesAnterior() {
    if (!doMesAnterior) return;
    if (!window.confirm(
      [
        "Repetir o treino de " + nomeDoMes(dados.mesAnterior) + " neste mês?",
        "Cada exercício começa na carga que a semana 4 atingiu.",
        temConteudo ? "O que estiver escrito aqui será substituído." : null,
      ].filter(Boolean).join(QUEBRA_DUPLA)
    )) return;

    const dias = {};
    DIAS.forEach((d) => {
      const bruto = (doMesAnterior.dias || {})[d.chave] || {};
      const linhas = Array.isArray(bruto.linhas) ? bruto.linhas : [];
      dias[d.chave] = {
        observacao: bruto.observacao || "",
        linhas: linhas.map((l) => {
          const semanas = semanasDaLinha(l);
          const ultima = semanas[semanas.length - 1];
          return {
            ...LINHA_NOVA, ...l,
            // A carga de partida é onde o mês anterior terminou. Sem
            // número na última semana, a base é mantida: "peso do corpo"
            // continua sendo peso do corpo.
            carga: lerCarga(ultima).numero !== null ? ultima : (l.carga || ""),
            semanas: ["", "", "", ""],
          };
        }),
      };
    });

    const novo = { dias, semanasExecutadas: [false, false, false, false] };
    setConteudo(novo);
    agendarSalvamento(novo);
  }

  function copiarDeOutro(origemAlvo) {
    const origem = (dados.planilhas || []).find(
      (x) => chave(x.modalidade) === chave(modalidadeAtiva) && (x.alvo || "turma") === origemAlvo
    );
    if (!origem) return;
    const de = origemAlvo === "turma" ? "da turma" : "de " + nomeDoAlvo(dados, origemAlvo);
    if (!window.confirm(
      "Copiar o treino " + de + " para " + (alvo === "turma" ? "a turma" : nomeDoAlvo(dados, alvo)) + "?"
      + "\n\nO que estiver escrito aqui será substituído."
    )) return;
    const novo = { dias: diasDe(origem) };
    setConteudo(novo);
    agendarSalvamento(novo);
    setCopiarDe("");
  }

  function mudarDia(dia, patch) {
    setConteudo((atual) => {
      const novo = { ...atual, dias: { ...atual.dias, [dia]: { ...atual.dias[dia], ...patch } } };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function mudarLinha(dia, i, patch) {
    setConteudo((atual) => {
      const linhas = atual.dias[dia].linhas.map((l, k) => (k === i ? { ...l, ...patch } : l));
      const novo = { ...atual, dias: { ...atual.dias, [dia]: { ...atual.dias[dia], linhas } } };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // A semana digitada substitui a conta naquela célula, e só naquela.
  //
  // A progressão é uma previsão, e nem todo atleta consegue subir carga na
  // semana prevista. Sem poder corrigir a célula, o preparador tinha duas
  // saídas ruins: mudar a carga base, que refaz as outras três semanas, ou
  // deixar no papel um número que não foi o que aconteceu. Campo vazio volta
  // a ser a conta, que é como se desfaz a correção.
  function mudarSemana(dia, i, k, valor) {
    setConteudo((atual) => {
      const linhas = atual.dias[dia].linhas.map((l, idx) => {
        if (idx !== i) return l;
        const semanas = [...(l.semanas || ["", "", "", ""])];
        semanas[k] = valor;
        return { ...l, semanas };
      });
      const novo = { ...atual, dias: { ...atual.dias, [dia]: { ...atual.dias[dia], linhas } } };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // O exercício novo entra no fim do BLOCO, e não no fim da lista: a ordem
  // gravada é a ordem impressa, e um aquecimento listado depois do
  // agachamento seria erro de leitura para quem recebe a folha.
  function adicionarLinha(dia, bloco) {
    setConteudo((atual) => {
      const linhas = [...atual.dias[dia].linhas];
      const ordem = BLOCOS.map((b) => b.chave);
      let posicao = linhas.length;
      for (let k = linhas.length - 1; k >= 0; k -= 1) {
        if (ordem.indexOf(linhas[k].bloco) <= ordem.indexOf(bloco)) { posicao = k + 1; break; }
        posicao = k;
      }
      linhas.splice(posicao, 0, { ...LINHA_NOVA, bloco });
      const novo = { ...atual, dias: { ...atual.dias, [dia]: { ...atual.dias[dia], linhas } } };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function removerLinha(dia, i) {
    setConteudo((atual) => {
      const linhas = atual.dias[dia].linhas.filter((_, k) => k !== i);
      const novo = { ...atual, dias: { ...atual.dias, [dia]: { ...atual.dias[dia], linhas } } };
      agendarSalvamento(novo);
      return novo;
    });
  }

  // Troca de lugar com o vizinho do mesmo bloco. Fora do bloco não troca:
  // subir um alongamento para dentro do aquecimento mudaria a sessão sem
  // que ninguém tivesse pedido.
  function moverLinha(dia, i, passo) {
    setConteudo((atual) => {
      const linhas = [...atual.dias[dia].linhas];
      const j = i + passo;
      if (j < 0 || j >= linhas.length) return atual;
      if (linhas[j].bloco !== linhas[i].bloco) return atual;
      [linhas[i], linhas[j]] = [linhas[j], linhas[i]];
      const novo = { ...atual, dias: { ...atual.dias, [dia]: { ...atual.dias[dia], linhas } } };
      agendarSalvamento(novo);
      return novo;
    });
  }

  function copiarDia(de, para) {
    if (!window.confirm(
      `Copiar o treino de ${rotuloDoDia(de)} para ${rotuloDoDia(para)}?\n\nO que estiver lá será substituído.`
    )) return;
    setConteudo((atual) => {
      const origem = atual.dias[de];
      const novo = {
        ...atual,
        dias: {
          ...atual.dias,
          [para]: {
            observacao: origem.observacao,
            linhas: origem.linhas.map((l) => ({ ...l })),
          },
        },
      };
      agendarSalvamento(novo);
      return novo;
    });
    setDiaAtivo(para);
  }

  async function baixarPDF() {
    setBaixando(true);
    try {
      await gerarPDFPlanilhaTreino({
        planilha: conteudo,
        modalidade: modalidadeAtiva,
        anoMes,
        alvoNome: alvo === "turma" ? "" : nomeDoAlvo(dados, alvo),
        contexto: contextoDaModalidade(dados, modalidadeAtiva),
        nucleo: userNucleo,
        nucleoNome: nomeNucleo(userNucleo),
        preparador: planilha ? planilha.atualizadoPorNome : "",
      });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Não foi possível gerar o PDF: " + (e.message || "") });
    } finally {
      setBaixando(false);
    }
  }

  if (!dados || !conteudo) {
    return <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando…</div></Card>;
  }

  if (!(dados.modalidades || []).length) {
    return (
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15.5 }}>Nenhuma modalidade cadastrada</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 6, lineHeight: 1.6 }}>
          A planilha de treino é por modalidade. Peça à coordenação para cadastrar as modalidades
          do núcleo.
        </div>
      </Card>
    );
  }

  const contexto = contextoDaModalidade(dados, modalidadeAtiva);
  const dia = conteudo.dias[diaAtivo] || { linhas: [], observacao: "" };
  const resumo = resumoDoDia(dia);
  // Os atletas oferecidos no "Vale para" são os da modalidade aberta, e só
  // eles. O servidor já manda os das modalidades que a pessoa enxerga; o
  // recorte por modalidade é feito aqui porque é o botão de cima que diz
  // qual delas está em tela.
  const atletas = (dados.atletas || []).filter((a) => chave(a.modalidade) === chave(modalidadeAtiva));
  const execucao = execucoesDoAtleta({
    execucoes, nome: nomeDoAlvo(dados, alvo), anoMes, chaveDoDia: diaAtivo,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {erro && <StatusBox tipo="erro" msg={erro} />}
      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      <Card>
        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: TEXTO }}>Planilha de treino</div>
          </div>
          <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
            {salvando
              ? <span style={{ fontSize: 12, color: AZUL }}>Salvando…</span>
              : salvoEm
                ? <span style={{ fontSize: 11, fontWeight: 800, color: OK, background: OK_CL, padding: "3px 10px", borderRadius: 20 }}>
                  Salvo às {salvoEm.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </span>
                : null}
            {/* Salva sozinho depois da pausa de digitação, e o botão existe
                assim mesmo: quem acabou de montar um treino inteiro quer
                fechar a tela sabendo que está guardado, e não confiando. */}
            {/* Fica aqui, com as outras ações da planilha, e não junto do
                "copiar treino de": aquele bloco só aparece quando já
                existe planilha no mês, e o mês vazio é exatamente onde
                este botão serve. */}
            {!somenteLeitura && doMesAnterior && (
              <Btn variant={temConteudo ? "discreto" : "secundario"} tamanho="sm"
                onClick={repetirMesAnterior}
                title={`Traz os exercícios de ${nomeDoMes(dados.mesAnterior)}, com a carga que a semana 4 atingiu.`}>
                ↻ Repetir {nomeDoMes(dados.mesAnterior)}
              </Btn>
            )}
            {!somenteLeitura && (
              <button onClick={salvarAgora} disabled={salvando} style={{
                background: AZUL, border: "none", borderRadius: 8,
                padding: "6px 15px", fontSize: 12.5, fontWeight: 700, color: BRANCO,
                cursor: salvando ? "wait" : "pointer", fontFamily: "inherit",
              }}>
                {salvando ? "Salvando…" : "Salvar"}
              </button>
            )}
            <button onClick={baixarPDF} disabled={baixando} style={{
              background: "none", border: `1px solid ${BORDA}`, borderRadius: 8,
              padding: "5px 11px", fontSize: 12, fontWeight: 600, color: TEXTO,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              {baixando ? "…" : "📄 Baixar em PDF"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(dados.modalidades || []).map((m) => {
              const ativa = chave(m) === chave(modalidadeAtiva);
              return (
                <button key={m} onClick={() => setModalidadeAtiva(m)} style={{
                  background: ativa ? AZUL : BRANCO, color: ativa ? BRANCO : CINZA_ESC,
                  border: `1.5px solid ${ativa ? AZUL : "#E5E7EB"}`, borderRadius: 9,
                  padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                }}>{m}</button>
              );
            })}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>Mês</div>
              <select value={anoMes} onChange={(e) => setAnoMes(e.target.value)} style={{
                border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 11px",
                fontSize: 13.5, fontFamily: "inherit", background: BRANCO, cursor: "pointer",
              }}>
                {mesesDisponiveis(dados.hojeISO).map((m) => (
                  <option key={m} value={m}>{nomeDoMes(m)}</option>
                ))}
              </select>
            </div>
            <div>
              {/* O rótulo diz a modalidade porque a lista muda com ela. Sem
                  isso, quem troca de modalidade e vê a lista de nomes mudar
                  junto não sabe se a tela filtrou ou se perdeu alguém. */}
              <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
                Vale para{modalidadeAtiva ? ` · ${modalidadeAtiva}` : ""}
              </div>
              <select value={alvo} onChange={(e) => setAlvo(e.target.value)} style={{
                border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 11px",
                fontSize: 13.5, fontFamily: "inherit", background: BRANCO, cursor: "pointer", minWidth: 220,
              }}>
                <option value="turma">Turma toda ({atletas.length} atleta(s))</option>
                {atletas.map((a) => {
                  // Quem já tem treino próprio aparece marcado. Sem a marca,
                  // descobrir isso exigia abrir atleta por atleta, e o
                  // treino individual é justamente a exceção que se procura.
                  const proprio = (dados.planilhas || []).some(
                    (x) => x.alvo === a.uid && chave(x.modalidade) === chave(modalidadeAtiva)
                  );
                  return (
                    <option key={a.uid} value={a.uid}>
                      {a.nome}{proprio ? " · treino próprio" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* O que está gravado: quando, e por quem. Sem isto não há como
            saber se a planilha aberta é a de ontem ou a que alguém acabou
            de mexer na sala ao lado. */}
        <div style={{
          display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
          marginTop: 12, fontSize: 12, color: CINZA_ESC,
        }}>
          {planilha && planilha.atualizadoEm ? (
            <span>
              Treino salvo em <b style={{ color: TEXTO }}>{dataHoraCurta(planilha.atualizadoEm)}</b>
              {planilha.atualizadoPorNome ? <> por {planilha.atualizadoPorNome}</> : null}
              {/* O convite para editar é de quem edita. Dito a quem só lê,
                  manda a coordenação tentar uma coisa que a tela não deixa
                  fazer, e a culpa da tentativa frustrada fica com ela. */}
              {somenteLeitura ? null : <>{" "}· dá para editar e salvar de novo.</>}
            </span>
          ) : herdadoDaTurma ? (
            <span style={{ color: ATENCAO }}>
              Este é o treino da turma. Ajuste o que mudar para{" "}
              <b>{nomeDoAlvo(dados, alvo)}</b> e salve: vira o treino individual dele, e o da
              turma continua como está.
            </span>
          ) : (
            <span>Nenhum treino salvo ainda para {alvo === "turma" ? "a turma" : nomeDoAlvo(dados, alvo)} neste mês.</span>
          )}

          {!somenteLeitura && (dados.planilhas || []).length > 0 && (
            <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              <span>Copiar treino de</span>
              <select value={copiarDe} onChange={(e) => { setCopiarDe(e.target.value); if (e.target.value) copiarDeOutro(e.target.value); }}
                style={{
                  border: "1px solid #D1D5DB", borderRadius: 8, padding: "6px 10px",
                  fontSize: 12.5, fontFamily: "inherit", background: BRANCO, cursor: "pointer",
                }}>
                <option value="">— escolha —</option>
                {alvo !== "turma" && (dados.planilhas || []).some((x) => chave(x.modalidade) === chave(modalidadeAtiva) && (x.alvo || "turma") === "turma") && (
                  <option value="turma">Turma toda</option>
                )}
                {atletas
                  .filter((a) => a.uid !== alvo)
                  .filter((a) => (dados.planilhas || []).some((x) => chave(x.modalidade) === chave(modalidadeAtiva) && x.alvo === a.uid))
                  .map((a) => <option key={a.uid} value={a.uid}>{a.nome}</option>)}
              </select>
            </span>
          )}
        </div>

        {(contexto.foco || (contexto.microciclo && contexto.microciclo[diaAtivo])) && (
          <div style={{
            background: AZUL_CLARO, border: "1.5px solid #BFD0EC", borderRadius: 10,
            padding: "11px 15px", fontSize: 12.5, color: AZUL_ESCURO, lineHeight: 1.6, marginTop: 14,
          }}>
            <b>Do plano do trimestre:</b>
            {contexto.foco ? <> objetivo do mês <b>{contexto.foco}</b></> : null}
            {contexto.microciclo && contexto.microciclo[diaAtivo]
              ? <> · microciclo de {rotuloDoDia(diaAtivo).toLowerCase()} <b>{contexto.microciclo[diaAtivo]}</b></>
              : null}
            . A planilha detalha o que o plano combinou.
          </div>
        )}
      </Card>

      {/* ── O que existe no mês ──────────────────────────────
          Só para quem lê, que é a coordenação e o técnico. O preparador
          escreve uma planilha de cada vez e já sabe qual abriu; quem lê
          chega sem saber o que foi entregue, e sem esta lista teria de
          abrir modalidade por modalidade, e dentro de cada uma atleta por
          atleta, para descobrir onde há treino escrito. */}
      {somenteLeitura && (
        <Card>
          <div style={{ fontSize: 15.5, fontWeight: 800, color: TEXTO }}>
            Treinos já inseridos em {nomeDoMes(anoMes)}
          </div>
          <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 5, lineHeight: 1.55 }}>
            Tudo que a preparação física escreveu neste mês. Clique para abrir qualquer um.
          </div>

          {(dados.planilhas || []).length === 0 ? (
            <div style={{
              marginTop: 14, padding: "20px 18px", background: FUNDO_SUAVE,
              border: `1px dashed ${BORDA}`, borderRadius: 10,
              fontSize: 13, color: CINZA_ESC, textAlign: "center", lineHeight: 1.6,
            }}>
              Nenhum treino escrito neste mês ainda.
              {" "}Troque o mês acima para ver os anteriores.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {ordenarPlanilhas(dados, dados.planilhas).map((p) => {
                const alvoDesta = p.alvo || "turma";
                const aberta = chave(p.modalidade) === chave(modalidadeAtiva) && alvoDesta === alvo;
                const diasComTreino = DIAS.filter((d) => resumoDoDia(p.dias && p.dias[d.chave]).exercicios > 0);
                const exercicios = DIAS.reduce(
                  (t, d) => t + resumoDoDia(p.dias && p.dias[d.chave]).exercicios, 0
                );
                return (
                  <button
                    key={p.id}
                    onClick={() => { setModalidadeAtiva(p.modalidade); setAlvo(alvoDesta); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                      textAlign: "left", width: "100%", font: "inherit", cursor: "pointer",
                      background: aberta ? AZUL_CLARO : BRANCO,
                      border: `1.5px solid ${aberta ? "#BFD0EC" : BORDA}`,
                      borderRadius: 10, padding: "11px 14px",
                    }}
                  >
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: TEXTO }}>
                        {p.modalidade}
                        <span style={{ color: CINZA_ESC, fontWeight: 600 }}>
                          {" · "}
                          {alvoDesta === "turma"
                            ? "Turma toda"
                            : (p.alvoNome || nomeDoAlvo(dados, alvoDesta) || "Atleta")}
                        </span>
                      </span>
                      <span style={{ display: "block", fontSize: 11.5, color: CINZA_ESC, marginTop: 3 }}>
                        {p.atualizadoEm
                          ? <>Salvo em {dataHoraCurta(p.atualizadoEm)}{p.atualizadoPorNome ? ` por ${p.atualizadoPorNome}` : ""}</>
                          : "Sem data de gravação"}
                      </span>
                    </span>

                    {/* Os dias com treino escrito, pelo nome. O dia que ficou
                        vazio aparece apagado em vez de sumir: é a diferença
                        entre "não treina quarta" e "faltou escrever quarta",
                        e quem lê precisa ver as duas. */}
                    <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {DIAS.map((d) => {
                        const temTreino = resumoDoDia(p.dias && p.dias[d.chave]).exercicios > 0;
                        return (
                          <span key={d.chave} style={{
                            fontSize: 10.5, fontWeight: 800, letterSpacing: ".03em",
                            textTransform: "uppercase", borderRadius: 6, padding: "3px 7px",
                            background: temTreino ? OK_CL : FUNDO_SUAVE,
                            color: temTreino ? OK : "#B6BECB",
                            border: `1px solid ${temTreino ? OK_CL : BORDA}`,
                          }}>{d.chave}</span>
                        );
                      })}
                    </span>

                    <span style={{
                      fontSize: 12, fontWeight: 700, color: diasComTreino.length ? TEXTO : ATENCAO,
                      whiteSpace: "nowrap",
                    }}>
                      {diasComTreino.length
                        ? `${diasComTreino.length} dia(s) · ${exercicios} exercício(s)`
                        : "Em branco"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Card>
        {/* Uma aba por dia, com quantos exercícios cada um tem. O número
            evita abrir os cinco dias para achar o que falta escrever. */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          {DIAS.map((d) => {
            const ativo = d.chave === diaAtivo;
            const r = resumoDoDia(conteudo.dias[d.chave]);
            return (
              <button key={d.chave} onClick={() => setDiaAtivo(d.chave)} style={{
                background: ativo ? AZUL_CLARO : BRANCO,
                color: ativo ? AZUL_ESCURO : CINZA_ESC,
                border: `1.5px solid ${ativo ? "#BFD0EC" : BORDA}`,
                borderRadius: "9px 9px 0 0", padding: "9px 18px", fontSize: 13,
                fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}>
                {d.curto}{" "}
                <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.8 }}>
                  {r.exercicios || "—"}
                </span>
              </button>
            );
          })}
          {!somenteLeitura && (
            <select value="" onChange={(e) => { if (e.target.value) copiarDia(diaAtivo, e.target.value); }}
              style={{
                marginLeft: "auto", border: `1px solid ${BORDA}`, borderRadius: 8,
                padding: "6px 10px", fontSize: 12, fontFamily: "inherit", cursor: "pointer", background: BRANCO,
              }}>
              <option value="">Copiar {rotuloDoDia(diaAtivo).toLowerCase()} para…</option>
              {DIAS.filter((d) => d.chave !== diaAtivo).map((d) => (
                <option key={d.chave} value={d.chave}>{d.curto}</option>
              ))}
            </select>
          )}
        </div>

        {/* ── Quantas vezes este atleta fez o treino do dia ──────
            Só com um atleta aberto. Na turma toda a tela mostraria trinta
            contagens de uma vez, e a planilha, que é o assunto, viraria
            rodapé da própria tela. */}
        {alvo !== "turma" && (
          <div style={{
            background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: 10,
            padding: "11px 14px", marginBottom: 12,
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            {/* A frase inteira num texto só, sem negrito no meio. Partida
                em três elementos, ela lê igual e deixa de ser procurável
                como frase, por quem testa e por quem usa Ctrl+F na tela. */}
            <span>
              <span style={{
                display: "block", fontSize: 13, fontWeight: 700,
                color: execucao.presentes > 0 ? OK : ATENCAO,
              }}>
                {`${nomeDoAlvo(dados, alvo) || "Este atleta"} executou o treino de `}
                {`${rotuloDoDia(diaAtivo).toLowerCase()} ${execucao.presentes} vez(es)`}
              </span>
              <span style={{ display: "block", fontSize: 11.5, color: CINZA_ESC, marginTop: 2 }}>
                {`Em ${nomeDoMes(anoMes)} · ${execucao.marcadas} de ${execucao.datas.length} com marcação de presença`}
              </span>
            </span>

            {/* Cada data do mês que cai nesse dia da semana. Verde fez,
                vermelho faltou, e o traço é data sem marcação nenhuma: não
                é falta, é registro que não aconteceu, e transformar uma
                coisa na outra inventa falta no histórico de alguém. */}
            <span style={{ display: "flex", gap: 5, flexWrap: "wrap", marginLeft: "auto" }}>
              {execucao.datas.length === 0 ? (
                <span style={{ fontSize: 11.5, color: CINZA_ESC }}>
                  Nenhuma {rotuloDoDia(diaAtivo).toLowerCase()} neste mês.
                </span>
              ) : execucao.datas.map((d) => {
                const fez = d.status === "presente";
                const faltou = d.status === "falta";
                return (
                  <span key={d.data} title={d.status || "sem marcação"} style={{
                    fontSize: 11, fontWeight: 800, borderRadius: 6, padding: "3px 7px",
                    fontVariantNumeric: "tabular-nums",
                    background: fez ? OK_CL : faltou ? "#FEE2E2" : BRANCO,
                    color: fez ? OK : faltou ? PERIGO : "#B6BECB",
                    border: `1px solid ${fez ? OK_CL : faltou ? "#FEE2E2" : BORDA}`,
                  }}>
                    {d.data.slice(8)}/{d.data.slice(5, 7)}
                  </span>
                );
              })}
            </span>

            <span style={{ fontSize: 11, color: CINZA_ESC, width: "100%", lineHeight: 1.5 }}>
              Vem da presença do treino físico. Quem marca é a preparação física, na aba
              Presença do treino físico, e aqui é só leitura.
            </span>
          </div>
        )}

        <div style={{ overflowX: "auto", border: `1px solid ${BORDA}`, borderRadius: 11 }}>
          <div style={{ minWidth: doMesAnterior ? 1340 : 1080 }}>
            <Cabecalho
              comAnterior={Boolean(doMesAnterior)}
              mesAnterior={dados.mesAnterior}
              executadas={executadas}
              onExecutada={marcarExecutada}
              somenteLeitura={somenteLeitura}
            />

            {BLOCOS.map((b) => {
              const indices = dia.linhas
                .map((l, i) => ({ l, i }))
                .filter(({ l }) => (l.bloco || "principal") === b.chave);
              return (
                <div key={b.chave}>
                  <div style={{
                    background: "#EEF2F8", color: AZUL_ESCURO, fontSize: 10.5, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: ".05em", padding: "7px 10px",
                  }}>{b.rotulo}</div>

                  {indices.map(({ l, i }) => {
                    // Com as correções já aplicadas: a semana digitada
                    // é o ponto de partida da progressão dali em diante.
                    const semanas = semanasDaLinha(l);
                    const calculadas = semanasDaCarga(l.carga, l.progressao);
                    return (
                      <div key={i} style={{
                        display: "grid", gridTemplateColumns: gradeDaLinha(Boolean(doMesAnterior)),
                        borderBottom: `1px solid ${BORDA}`, alignItems: "stretch",
                      }}>
                        {COLUNAS.map((c) => (
                          <input key={c.chave} disabled={somenteLeitura}
                            value={l[c.chave] || ""} placeholder={c.dica || ""}
                            onChange={(e) => mudarLinha(diaAtivo, i, { [c.chave]: e.target.value })}
                            style={{ ...estiloCelula, textAlign: c.centro ? "center" : "left" }} />
                        ))}
                        {doMesAnterior && (() => {
                          const antiga = linhaDoMesAnterior(doMesAnterior, diaAtivo, l.exercicio);
                          const valores = antiga ? semanasDaLinha(antiga) : ["", "", "", ""];
                          return Array.from({ length: SEMANAS }, (_, k) => (
                            <div key={`ant${k}`} title={antiga
                              ? `Semana ${k + 1} de ${dados.mesAnterior}. Mês fechado.`
                              : "Este exercício não estava na planilha do mês anterior."}
                              style={{
                                ...estiloCelula, background: "#F1F3F6", color: CINZA_ESC,
                                textAlign: "center", fontVariantNumeric: "tabular-nums",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                              {valores[k] || "—"}
                            </div>
                          ));
                        })()}

                        {Array.from({ length: SEMANAS }, (_, k) => {
                          const digitado = (l.semanas || [])[k] || "";
                          const travada = executadas[k];
                          return (
                            <input key={k} disabled={somenteLeitura || travada}
                              value={digitado}
                              placeholder={semanas[k] || "—"}
                              title={calculadas.length
                                ? `Calculado: ${calculadas[k]}. Escreva aqui para usar outra carga nesta semana, e as seguintes passam a subir a partir dela.`
                                : "Escreva a carga desta semana."}
                              onChange={(e) => mudarSemana(diaAtivo, i, k, e.target.value)}
                              style={{
                                ...estiloCelula,
                                background: travada ? "#E9F1E9" : FUNDO_SUAVE, textAlign: "center",
                                fontVariantNumeric: "tabular-nums",
                                color: AZUL_ESCURO, fontWeight: digitado ? 700 : 400,
                                cursor: travada ? "not-allowed" : "auto",
                              }} />
                          );
                        })}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                          {!somenteLeitura && (
                            <>
                              <button onClick={() => moverLinha(diaAtivo, i, -1)} title="Subir"
                                style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: 11, padding: "2px 3px" }}>▲</button>
                              <button onClick={() => moverLinha(diaAtivo, i, 1)} title="Descer"
                                style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: 11, padding: "2px 3px" }}>▼</button>
                              <button onClick={() => removerLinha(diaAtivo, i)} title="Remover"
                                style={{ background: "none", border: "none", color: PERIGO, cursor: "pointer", fontSize: 13, padding: "2px 4px" }}>✕</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {!somenteLeitura && (
                    <div style={{ padding: "7px 10px", borderBottom: `1px solid ${BORDA}` }}>
                      <button onClick={() => adicionarLinha(diaAtivo, b.chave)} style={{
                        background: "none", border: "none", color: AZUL, fontSize: 12,
                        fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit",
                      }}>
                        + exercício {b.ondeEntra}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 11 }}>
          <div style={{ fontSize: 12, color: CINZA_ESC }}>
            {resumo.exercicios} exercício(s) · <b>{resumo.series} série(s)</b> no dia
          </div>
          <div style={{ fontSize: 11.5, color: CINZA_ESC }}>
            As semanas em cinza são conta, feita da carga base com a porcentagem. Escreva por
            cima quando a carga do dia não for a prevista; apagar devolve a conta.
          </div>
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 11, color: CINZA_ESC, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 5 }}>
          Observações de {rotuloDoDia(diaAtivo).toLowerCase()}
        </div>
        <input disabled={somenteLeitura} value={dia.observacao || ""}
          onChange={(e) => mudarDia(diaAtivo, { observacao: e.target.value })}
          placeholder="Ex: quem competiu no fim de semana faz só o aquecimento e a volta à calma."
          style={{
            width: "100%", border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 11px",
            fontSize: 13.5, fontFamily: "inherit", boxSizing: "border-box", color: TEXTO, background: BRANCO,
          }} />
        <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 9, lineHeight: 1.55 }}>
          Sai no rodapé da folha impressa do dia.
        </div>
      </Card>

      {somenteLeitura && (
        <div style={{ fontSize: 12, color: CINZA_ESC, textAlign: "center" }}>
          Você está vendo a planilha da preparação física. Só ela preenche.
        </div>
      )}
    </div>
  );
}

function rotuloDoDia(chaveDoDia) {
  const d = DIAS.find((x) => x.chave === chaveDoDia);
  return d ? d.longo : chaveDoDia;
}

function contextoDaModalidade(dados, modalidade) {
  const mapa = (dados && dados.contexto) || {};
  const achada = Object.keys(mapa).find((m) => chave(m) === chave(modalidade));
  return achada ? mapa[achada] : {};
}

function nomeDoAlvo(dados, uid) {
  const a = ((dados && dados.atletas) || []).find((x) => x.uid === uid);
  return a ? a.nome : "";
}

// A lista do mês: por modalidade, e dentro dela a turma antes dos
// individuais. A turma primeiro porque o treino individual é o desvio, e
// desvio só se entende depois de ver do que ele desvia.
function ordenarPlanilhas(dados, planilhas) {
  return [...(planilhas || [])].sort((a, b) => {
    const porModalidade = String(a.modalidade || "").localeCompare(String(b.modalidade || ""), "pt-BR");
    if (porModalidade !== 0) return porModalidade;
    const turmaA = (a.alvo || "turma") === "turma" ? 0 : 1;
    const turmaB = (b.alvo || "turma") === "turma" ? 0 : 1;
    if (turmaA !== turmaB) return turmaA - turmaB;
    const nomeA = a.alvoNome || nomeDoAlvo(dados, a.alvo) || "";
    const nomeB = b.alvoNome || nomeDoAlvo(dados, b.alvo) || "";
    return nomeA.localeCompare(nomeB, "pt-BR");
  });
}
